import * as React from "react";

import { CloseOutlined } from "@ant-design/icons";
import { AsyncComponent } from "@impero-com/common/components/asyncComponent";
import {
    ColumnBuilder,
    DataTable,
} from "@impero-com/common/components/dataTable";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    ArrayDataSource,
    DataExtractor,
    DataView,
} from "@impero-com/common/utils/dataView";
import { parseDate } from "@impero-com/common/utils/date";
import { formatDateTimeFast } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Button, InformationModal, Modal, PageSpinner } from "@impero-com/ui";
import { Input, Popconfirm, Spin, notification } from "antd";
import memoizeOne from "memoize-one";
import moment, { Moment } from "moment-timezone";

import type { UserProfile } from "api/generated/model";
import {
    PersonalAccessTokenListDto,
    PersonalAccessTokenDtoContent,
    InsertablePersonalAccessToken,
    PersonalAccessTokenWithSecretDto,
} from "api/generated/model";
import {
    deletePersonalAccessTokensTokenId,
    postPersonalAccessTokens,
} from "api/generated/personal-access-tokens/personal-access-tokens";
import { getUsersUserIdPersonalAccessTokens } from "api/generated/users/users";
import { DatePicker } from "components/datePicker";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { StringInput } from "components/form/legacyFields/stringInput";
import { CONSTANTS } from "utils/constants";
import {
    FormCreate,
    FormRootComponentProps,
    hasError,
    mapForm,
    ObjectError,
    simpleMappers,
} from "utils/form";

import * as styles from "./personalAccessTokens.css";
import * as theme from "./theme.css";

export interface PersonalAccessTokensProps {
    ajaxManager: AjaxManager;
    orgProfileCookie: UserProfile | null;
}

type Status =
    | { type: "DEFAULT" }
    | { type: "CREATE" }
    | { type: "SUBMITTING" }
    | { type: "DISPLAY_KEY"; key: string }
    | { type: "ERROR"; code: number | undefined };

export interface PersonalAccessTokensState {
    status: Status;
    counter: number;
}

type Dto = PersonalAccessTokenListDto;

export class PersonalAccessTokens extends React.PureComponent<
    PersonalAccessTokensProps,
    PersonalAccessTokensState
> {
    private asyncComponent = React.createRef<AsyncComponent<Dto>>();

    state: PersonalAccessTokensState = {
        status: { type: "DEFAULT" },
        counter: 0,
    };

    private memoizeColumnBuilders: (
        dto: Dto
    ) => Record<string, ColumnBuilder<PersonalAccessTokenDtoContent, any>> =
        memoizeOne((_dto) => {
            return {
                delete: (
                    _extractor: DataExtractor<
                        PersonalAccessTokenDtoContent,
                        string
                    >
                ) => ({
                    getFormattedValue: (row: PersonalAccessTokenDtoContent) => {
                        return (
                            <Popconfirm
                                title={`${t("PersonalAccessToken_AskDeleteKey")}`}
                                okText={t("Classification_Mapping_InUse_Yes")}
                                cancelText={t(
                                    "Classification_Mapping_InUse_No"
                                )}
                                onConfirm={() => {
                                    this.deleteEntry(row.resource.id);
                                }}
                                placement={"topRight"}
                            >
                                <Button
                                    id={`delete-pat-${row.resource.id}`}
                                    title={t("PersonalAccessToken_DeleteKey")}
                                    size="icon"
                                    className={styles.deleteKeyButton}
                                    icon={<CloseOutlined />}
                                />
                            </Popconfirm>
                        );
                    },
                    display: "DEFAULT_VISIBLE",
                    sortable: false,
                    layout: {
                        fixedWidth: 30,
                    },
                }),
            };
        });

    private memoizeDataView: (
        dto: Dto
    ) => DataView<PersonalAccessTokenDtoContent> = memoizeOne((dto) => {
        const stringCompare = Cmp.string();
        const standardExtractors: Array<
            DataExtractor<PersonalAccessTokenDtoContent, unknown>
        > = [
            {
                id: "name",
                label: t("PersonalAccessToken_List_Column_Name"),
                getValue: (row) => row.resource.name,
                compare: Cmp.map((row) => row.resource.name, stringCompare),
                asString: (row) => row.resource.name,
            },
            {
                id: "createdAt",
                label: t("PersonalAccessToken_List_Column_CreatedAt"),
                getValue: (row) => row.resource.createdAt,
                compare: Cmp.map(
                    (row) => row.resource.createdAt,
                    stringCompare
                ),
                asString: (row) =>
                    formatDateTimeFast(parseDate(row.resource.createdAt)),
            },
            {
                id: "lastUsedAt",
                label: t("PersonalAccessToken_List_Column_LastUsedAt"),
                getValue: (row) => row.resource.lastUsedAt,
                compare: Cmp.map(
                    (row) => row.resource.lastUsedAt,
                    Cmp.undefinedLast(stringCompare)
                ),
                asString: (row) =>
                    row.resource.lastUsedAt !== null &&
                    row.resource.lastUsedAt !== undefined
                        ? formatDateTimeFast(parseDate(row.resource.lastUsedAt))
                        : t("PersonalAccessToken_List_NeverUsed"),
            },
            {
                id: "expiresAt",
                label: t("PersonalAccessToken_List_Column_ExpiresAt"),
                getValue: (row) => row.resource.expiresAt,
                compare: Cmp.map(
                    (row) => row.resource.expiresAt,
                    Cmp.undefinedLast(stringCompare)
                ),
                asString: (row) =>
                    row.resource.expiresAt !== null &&
                    row.resource.expiresAt !== undefined
                        ? formatDateTimeFast(parseDate(row.resource.expiresAt))
                        : t("PersonalAccessToken_List_NeverExpires"),
            },
            {
                id: "delete",
                label: "",
                getValue: () => "",
                compare: Cmp.map(
                    (row) => row.resource.expiresAt,
                    Cmp.undefinedLast(stringCompare)
                ),
                asString: (row) =>
                    row.resource.expiresAt !== null &&
                    row.resource.expiresAt !== undefined
                        ? formatDateTimeFast(parseDate(row.resource.expiresAt))
                        : t("PersonalAccessToken_List_NeverExpires"),
            },
        ];

        return {
            dataSource: new ArrayDataSource(dto.content, (row) =>
                row.resource.id.toString()
            ),
            extractorGroups: [
                {
                    id: "standard",
                    label: "Standard",
                    extractors: standardExtractors,
                },
            ],
            sorters: [],
            defaultCompareItems: Cmp.map(
                (row) => row.resource.id,
                Cmp.primitive
            ),
        };
    });

    private async deleteEntry(id: number) {
        await deletePersonalAccessTokensTokenId(id);
        if (this.asyncComponent.current) {
            this.asyncComponent.current.reload();
        }
    }

    render = () => {
        const { ajaxManager, orgProfileCookie } = this.props;
        const { counter } = this.state;

        if (orgProfileCookie === null) {
            // We are not logged-in anymore
            return undefined;
        }

        const { userId } = orgProfileCookie;

        return (
            <AsyncComponent<Dto>
                ref={this.asyncComponent}
                ajaxManager={ajaxManager}
                key={`personal-access-tokens-${counter}`}
                promise={() => getUsersUserIdPersonalAccessTokens(userId)}
                renderContent={this.renderContent}
                renderError={(code) => <LoggedInHTTPError code={code} />}
                renderLoading={() => <PageSpinner />}
            />
        );
    };

    private renderContent = (dto: Dto) => {
        const dataView = this.memoizeDataView(dto);
        const columnBuilders = this.memoizeColumnBuilders(dto);
        return (
            <>
                {this.renderPopup()}
                <DataTable
                    id={"personal-access-tokens"}
                    dataView={dataView}
                    columnBuilders={columnBuilders}
                    footerRenderer={this.renderFooter}
                />
            </>
        );
    };

    private renderFooter = () => {
        return (
            <tr className="ant-table-row ant-table-row-level-0">
                <td colSpan={5}>
                    <Button
                        disabled={this.state.status.type === "SUBMITTING"}
                        id="create-personal-access-token"
                        variant="link"
                        onClick={() =>
                            this.setState({ status: { type: "CREATE" } })
                        }
                    >
                        {t("PersonalAccessToken_CreateKey")}
                    </Button>
                </td>
            </tr>
        );
    };

    private renderPopup = () => {
        const { status } = this.state;
        switch (status.type) {
            case "DEFAULT":
                return undefined;
            case "CREATE":
            case "SUBMITTING":
                return (
                    <PersonalAccessTokenForm
                        onCancel={this.cancelCreate}
                        onSubmit={this.create}
                    />
                );
            case "DISPLAY_KEY":
                return (
                    <InformationModal
                        open
                        onClose={this.closeDisplayKey}
                        type="success"
                        title={t("PersonalAccessToken_Display_Title")}
                        // Wider than a plain message: the key has to be readable.
                        width={520}
                        footer={
                            <Button
                                variant="primary"
                                onClick={this.closeDisplayKey}
                            >
                                {t("Close")}
                            </Button>
                        }
                    >
                        <>
                            {t("PersonalAccessToken_Display_Description")}
                            <Input
                                type="text"
                                readOnly
                                className={theme.displayKey}
                                onClick={(event) => {
                                    event.currentTarget.setSelectionRange(
                                        0,
                                        status.key.length
                                    );
                                    navigator.clipboard
                                        .writeText(status.key)
                                        .then(() => {
                                            notification.success({
                                                message: t(
                                                    "PersonalAccessToken_Display_CopiedToClipboard"
                                                ),
                                            });
                                        });
                                }}
                                value={status.key}
                            />
                        </>
                    </InformationModal>
                );
            case "ERROR":
                console.error(`ERROR with code ${status.code}`);
                return undefined;
            default:
                assertNever(status);
        }
    };

    private create = (payload: InsertablePersonalAccessToken) => {
        const { ajaxManager } = this.props;
        ajaxManager.ajax<PersonalAccessTokenWithSecretDto, Status>(
            () => postPersonalAccessTokens(payload),
            {
                component: this,
                initialStatusValue: "CREATE",
                inFlightStatus: { type: "SUBMITTING" },
                getSuccessStatus: ([response]) => {
                    this.setState({ counter: this.state.counter + 1 });
                    return {
                        type: "DISPLAY_KEY",
                        key: response.content.resource.key,
                    };
                },
                getErrorStatus: (code) => {
                    return {
                        type: "ERROR",
                        code,
                    };
                },
            }
        );
    };

    private cancelCreate = () => {
        if (this.state.status.type === "CREATE") {
            this.setState({ status: { type: "DEFAULT" } });
        }
    };

    private closeDisplayKey = () =>
        this.setState({ status: { type: "DEFAULT" } });
}

interface FormProps extends FormRootComponentProps<InsertablePersonalAccessToken> {
    onCancel: () => void;
    onSubmit: (payload: InsertablePersonalAccessToken) => void;
}

interface FormState {
    spinning: boolean;
}

class PersonalAccessTokenInternal extends React.Component<
    FormProps,
    FormState
> {
    state: FormState = { spinning: false };

    render = () => {
        const { form, onCancel } = this.props;
        const { spinning } = this.state;

        const expiresAtMappers = simpleMappers<
            "expiresAt",
            string | undefined,
            Moment | null,
            InsertablePersonalAccessToken
        >(
            "expiresAt",
            (expiresAtString) =>
                expiresAtString !== undefined ? moment(expiresAtString) : null,
            (expiresAtMoment) =>
                expiresAtMoment !== null
                    ? expiresAtMoment.toISOString()
                    : undefined
        );
        const expiresAtForm = mapForm<{ expiresAt: Moment | null }>()(
            form,
            expiresAtMappers()
        );

        return (
            <Modal
                variant="content"
                open
                onClose={onCancel}
                title={t("PersonalAccessToken_Form_Title")}
                footer={
                    <>
                        <Button onClick={onCancel}>{t("Cancel")}</Button>
                        <Button variant="primary" onClick={this.onSubmit}>
                            {t("Create")}
                        </Button>
                    </>
                }
            >
                <Spin spinning={spinning}>
                    <StringInput
                        id="name"
                        type="CREATE"
                        label={t("PersonalAccessToken_Form_Name")}
                        form={form}
                        validators={[
                            {
                                kind: "ruleset",
                                required: true,
                                displayField: t(
                                    "PersonalAccessToken_Form_Name"
                                ),
                                whitespace: true,
                                max: CONSTANTS.USER_CONSTANTS
                                    .MAX_API_KEY_NAME_LEN,
                            },
                        ]}
                        initialValue={undefined}
                    />
                    <BaseFormDisplay
                        label={t("PersonalAccessToken_Form_ExpiresAt")}
                    >
                        {expiresAtForm.getFieldDecorator(
                            "expiresAt",
                            {}
                        )(
                            <DatePicker
                                name={"expiresAt"}
                                id={"expiresAt"}
                                disabledDate={(date) =>
                                    date === null || date < moment()
                                }
                                placeholder={t(
                                    "PersonalAccessToken_Form_NeverExpires"
                                )}
                            />
                        )}
                    </BaseFormDisplay>
                </Spin>
            </Modal>
        );
    };

    private onSubmit = () => {
        const { form } = this.props;
        form.validateFields({ force: true }, (errors: ObjectError, values) => {
            if (!hasError(errors)) {
                if (this.state.spinning) {
                    return;
                }
                this.setState({ spinning: true });
                this.props.onSubmit(values);
            }
        });
    };
}

const PersonalAccessTokenForm = FormCreate<
    InsertablePersonalAccessToken,
    FormProps
>()(PersonalAccessTokenInternal);
